import {
  changeToAdminFlagged,
  checkVersionPart,
  flagPart,
  getFlaggedParts,
  unFlagParts,
  unflagAllMatchingFilter,
  type flagPartParam,
  type unflagAllParam,
} from "@/api/flaggedPartService";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import { queryKeys } from "@/lib/queryKeys";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetFlaggedParts = (
  params: flagPartParam,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.flaggedPart.list(params),
    queryFn: () => getFlaggedParts(params),
    staleTime: CACHE_TIME.SHORT,
    enabled,
  });
};

export const useFlagPart = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.flaggedPart.flagPart(),
    mutationFn: ({
      partId,
      flaggedReason,
      isFlagged,
    }: {
      partId: number;
      flaggedReason: Set<string>;
      isFlagged: boolean;
    }) => flagPart(partId, flaggedReason, isFlagged),
    onSuccess: () => {
      queryInvalidation.invalidatePart();
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useUnFlagParts = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.flaggedPart.unFlagParts(),
    mutationFn: ({
      partIds,
      reasonId,
      isAdminFlagged,
    }: {
      partIds: number[];
      reasonId: number;
      isAdminFlagged: boolean;
    }) => unFlagParts({ partIds, reason_id: reasonId, is_admin_flagged: isAdminFlagged }),
    onSuccess: () => {
      queryInvalidation.invalidatePart();
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useUnflagAllMatchingFilter = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.flaggedPart.unflagAll(),
    mutationFn: (params: unflagAllParam) => unflagAllMatchingFilter(params),
    onSuccess: () => {
      queryInvalidation.invalidatePart();
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useChangeToAdminFlagged = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.flaggedPart.changeToAdminFlagged(),
    mutationFn: (partIds: number[]) => changeToAdminFlagged(partIds),
    onSuccess: () => {
      queryInvalidation.invalidatePart();
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useCheckVersionPart = (partId: number) => {
  return useQuery({
    queryKey: queryKeys.flaggedPart.checkVersion(partId),
    queryFn: () => checkVersionPart(partId),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: !!partId,
  });
};